import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import ModelPicker from "./components/ModelPicker";
import { FileSignature, Plus, Trash2, HelpCircle, Gauge, Wand2, ArrowRight, Scale, AlertTriangle, CheckCircle2, BookMarked, Calculator, ShieldQuestion, GitBranch } from "lucide-react";

const SEV_COLOR = {
  "Drafting Blocker": { bg: "#fdecea", fg: "#a3271d" },
  "Source Conflict": { bg: "#fdecea", fg: "#a3271d" },
  "Jurisdiction Required": { bg: "#fff5e5", fg: "#8a5a00" },
  "Clarification Needed": { bg: "#fff5e5", fg: "#8a5a00" },
  "Professional Review Required": { bg: "#eef", fg: "#3a3a7a" },
  "Informational": { bg: "#eef7ee", fg: "#2f6b34" },
};

// ---- Jurisdiction picker (separate fields, verification status) ----
function JurisdictionPicker({ value, opts, onChange }) {
  if (!opts) return null;
  const groups = [
    ["— Special —", opts.special],
    ["U.S. States & DC", opts.us_states],
    ["U.S. Territories", opts.us_territories],
    ["Federal", opts.us_federal],
    ["Countries", opts.countries],
  ];
  return (
    <div className="flex items-center gap-2">
      <select value={value?.value || ""} onChange={e => onChange({ value: e.target.value, status: "User Entered" })} data-testid="jur-select">
        <option value="">Not selected</option>
        {groups.map(([label, arr]) => (
          <optgroup key={label} label={label}>{(arr || []).map(o => <option key={o} value={o}>{o}</option>)}</optgroup>
        ))}
      </select>
      {value?.value && <span className="chip text-xs" style={{ background: "#fff5e5", color: "#8a5a00" }}>{value.status || "User Entered"}</span>}
    </div>
  );
}

export function DraftingStudioPage() {
  const [catalog, setCatalog] = useState(null);
  const [sessions, setSessions] = useState([]);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([A.drafting.catalog(), A.drafting.sessions()]);
    setCatalog(c.data); setSessions(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async (instrument) => {
    try { const r = await A.drafting.createSession(instrument); nav(`/drafting/${r.data.id}`); }
    catch (e) { toast.error("Could not start intake"); }
  };
  const del = async (sid) => { if (window.confirm("Delete this intake session?")) { await A.drafting.delSession(sid); load(); } };

  if (!catalog) return <div>Loading…</div>;
  return (
    <div data-testid="drafting-studio-page">
      <H1>Guided Drafting</H1>
      <Sub>Question-first, jurisdiction-aware intake. Answer the questions for an instrument and the portal assembles a structured draft marked <b>DRAFT FOR ATTORNEY REVIEW</b>. The engine never invents facts — unknowns stay visible and professional review is always required.</Sub>

      <H2>Start a new intake</H2>
      <div className="grid md:grid-cols-3 gap-3 mb-8">
        {catalog.instruments.map(i => (
          <Card key={i.key} data-testid={`draft-instrument-${i.key}`}>
            <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><FileSignature size={15} /> {i.title}</div>
            <div className="text-xs mt-1 mb-3" style={{ color: 'var(--muted)' }}>{i.family}</div>
            <button className="btn-gold text-xs flex items-center gap-1" onClick={() => start(i.key)} data-testid={`start-${i.key}`}><Plus size={12} /> Begin intake</button>
          </Card>
        ))}
      </div>

      <H2>Your intake sessions</H2>
      <div className="space-y-2">
        {sessions.map(s => (
          <Card key={s.id} data-testid={`session-${s.id}`}>
            <div className="flex justify-between items-center gap-3">
              <div>
                <div className="font-medium" style={{ color: 'var(--primary)' }}>{s.title}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{s.instrument} · <span className="chip text-xs">{s.status}</span> · updated {(s.updated_at || "").slice(0, 10)}</div>
              </div>
              <div className="flex gap-1">
                <button className="btn-outline text-xs flex items-center gap-1" onClick={() => nav(`/drafting/${s.id}`)} data-testid={`open-${s.id}`}>Open <ArrowRight size={12} /></button>
                <button className="btn-outline text-xs" style={{ color: 'var(--overdue)' }} onClick={() => del(s.id)}><Trash2 size={12} /></button>
              </div>
            </div>
          </Card>
        ))}
        {sessions.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No sessions yet — start an intake above.</div></Card>}
      </div>
    </div>
  );
}

export function DraftingSessionPage() {
  const sid = window.location.pathname.split("/").pop();
  const [data, setData] = useState(null);
  const [jopts, setJopts] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [answers, setAnswers] = useState({});
  const [jur, setJur] = useState({});
  const [explain, setExplain] = useState({});
  const [model, setModel] = useState("claude");
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    const [q, j, r] = await Promise.all([A.drafting.questions(sid), A.drafting.jurisdictions(), A.drafting.readiness(sid)]);
    setData(q.data); setJopts(j.data); setReadiness(r.data);
    setAnswers(q.data.answers || {}); setJur(q.data.jurisdictions || {});
  }, [sid]);
  useEffect(() => { refresh(); }, [refresh]);

  const persist = async (nextAnswers, nextJur) => {
    await A.drafting.save(sid, { answers: nextAnswers, jurisdictions: nextJur });
    // recompute questions (conditional) + readiness
    const [q, r] = await Promise.all([A.drafting.questions(sid), A.drafting.readiness(sid)]);
    setData(q.data); setReadiness(r.data);
  };
  const setA = (k, v) => { const n = { ...answers, [k]: v }; setAnswers(n); };
  const blur = () => persist(answers, jur);
  const commit = (k, v) => { const n = { ...answers, [k]: v }; setAnswers(n); persist(n, jur); };
  const setJurField = async (k, v) => { const n = { ...jur, [k]: v }; setJur(n); await persist(answers, n); };

  const doExplain = async (k) => {
    setExplain({ ...explain, [k]: "…" });
    try { const r = await A.drafting.explain(sid, k, model); setExplain(e => ({ ...e, [k]: r.data.explanation })); }
    catch { setExplain(e => ({ ...e, [k]: "Explanation unavailable." })); }
  };
  const generate = async () => {
    try { const r = await A.drafting.generate(sid); toast.success("Draft generated"); nav(`/studio/${r.data.draft_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot generate yet"); }
  };

  if (!data) return <div>Loading…</div>;
  const categories = [];
  data.questions.forEach(q => { if (!categories.includes(q.category)) categories.push(q.category); });

  return (
    <div data-testid="drafting-session-page">
      <div className="flex justify-between items-start gap-3">
        <div><H1>{data.title}</H1><Sub>Question-first intake. Marked <b>{data.watermark}</b>. Answers save automatically; unknowns are fine — leave them blank.</Sub></div>
        <div className="shrink-0">
          <div className="label-caps mb-1">"Why?" AI Model</div>
          <ModelPicker value={model} onChange={setModel} testid="drafting-model-picker" />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {categories.map(cat => (
            <Card key={cat} data-testid={`cat-${cat.replace(/\s+/g, '-')}`}>
              <div className="font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--primary)' }}>
                {cat === "Jurisdictions" ? <Scale size={15} /> : null}{cat}
              </div>
              <div className="space-y-4">
                {data.questions.filter(q => q.category === cat).map(q => (
                  <div key={q.key} data-testid={`q-${q.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <label className="label-caps flex items-center gap-1">{q.prompt}{q.required && <span style={{ color: 'var(--overdue)' }}>*</span>}</label>
                      <button className="text-xs flex items-center gap-1" style={{ color: 'var(--gold, #8a6d1a)' }} onClick={() => doExplain(q.key)} data-testid={`explain-${q.key}`}><HelpCircle size={12} /> Why?</button>
                    </div>
                    {q.type === "jurisdiction"
                      ? <JurisdictionPicker value={jur[q.jfield]} opts={jopts} onChange={(v) => setJurField(q.jfield, v)} />
                      : q.type === "textarea" ? <textarea rows={2} value={answers[q.key] || ""} onChange={e => setA(q.key, e.target.value)} onBlur={blur} data-testid={`input-${q.key}`} />
                      : q.type === "select" ? <select value={answers[q.key] || ""} onChange={e => commit(q.key, e.target.value)} data-testid={`input-${q.key}`}><option value="">—</option>{q.options.map(o => <option key={o}>{o}</option>)}</select>
                      : q.type === "boolean" ? <label className="flex items-center gap-2 text-sm mt-1"><input type="checkbox" className="w-auto" checked={!!answers[q.key]} onChange={e => commit(q.key, e.target.checked)} data-testid={`input-${q.key}`} /> Yes</label>
                      : <input type={q.type === "percent" || q.type === "number" ? "number" : q.type === "date" ? "date" : "text"} value={answers[q.key] || ""} onChange={e => setA(q.key, e.target.value)} onBlur={blur} data-testid={`input-${q.key}`} />}
                    {q.review && <div className="text-xs mt-1" style={{ color: '#3a3a7a' }}>Triggers: {q.review}</div>}
                    {explain[q.key] && <div className="text-xs mt-2 p-2 rounded" style={{ background: "#faf8f2", color: 'var(--muted)' }}>{explain[q.key]}</div>}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Readiness sidebar */}
        <div>
          <Card data-testid="readiness-panel" className="sticky top-4">
            <div className="font-medium flex items-center gap-2 mb-2" style={{ color: 'var(--primary)' }}><Gauge size={16} /> Drafting Readiness</div>
            {readiness && (
              <>
                <div className="text-3xl font-display" style={{ color: 'var(--primary)' }}>{readiness.readiness_pct}%</div>
                <div className="text-xs mb-3" style={{ color: 'var(--muted)' }}>{readiness.answered_required}/{readiness.total_required} required answered · administrative indicator, not a validity score</div>

                {readiness.blocking_issues.length > 0 && <div className="mb-2">
                  <div className="text-xs font-medium mb-1 flex items-center gap-1" style={{ color: '#a3271d' }}><AlertTriangle size={12} /> Blocking</div>
                  {readiness.blocking_issues.map((i, idx) => <div key={idx} className="text-xs mb-1 p-1 rounded" style={{ background: SEV_COLOR[i.severity]?.bg, color: SEV_COLOR[i.severity]?.fg }}>{i.detail} <i>({i.prompt || i.key})</i></div>)}
                </div>}

                {readiness.nonblocking_issues.length > 0 && <div className="mb-2">
                  <div className="text-xs font-medium mb-1" style={{ color: '#8a5a00' }}>Notices</div>
                  {readiness.nonblocking_issues.map((i, idx) => <div key={idx} className="text-xs mb-1 p-1 rounded" style={{ background: SEV_COLOR[i.severity]?.bg || "#eee", color: SEV_COLOR[i.severity]?.fg || "#555" }}><b>{i.severity}:</b> {i.detail}</div>)}
                </div>}

                {readiness.professional_reviews.length > 0 && <div className="mb-2">
                  <div className="text-xs font-medium mb-1" style={{ color: '#3a3a7a' }}>Professional review</div>
                  {readiness.professional_reviews.map((r, idx) => <span key={idx} className="chip text-xs mr-1 mb-1 inline-block" style={{ background: "#eef", color: "#3a3a7a" }}>{r.review}</span>)}
                </div>}

                <div className="text-xs mb-3 p-2 rounded" style={{ background: "#faf8f2", color: 'var(--muted)' }}>{readiness.recommended_next_action}</div>

                <button className="btn-primary w-full flex items-center justify-center gap-1" disabled={!readiness.can_generate} onClick={generate} data-testid="generate-btn">
                  {readiness.can_generate ? <><Wand2 size={14} /> Generate draft</> : <>Resolve blockers to generate</>}
                </button>
                {readiness.jurisdiction_status.missing_material.length > 0 && readiness.can_generate &&
                  <div className="text-xs mt-2 flex items-center gap-1" style={{ color: '#8a5a00' }}><Scale size={11} /> Jurisdiction-specific language will be withheld until material jurisdictions are set.</div>}
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ==================== CLAUSE LIBRARY ====================
export function ClauseLibraryPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => { A.drafting.clauses().then(r => setRows(r.data)); }, []);
  if (!rows) return <div>Loading…</div>;
  const cats = [...new Set(rows.map(r => r.category))];
  return (
    <div data-testid="clause-library-page">
      <H1>Clause Library</H1>
      <Sub>Approved and placeholder clauses, tracked by category, applicable instruments, jurisdiction, and required review. Jurisdiction-specific clauses stay as placeholders until the governing jurisdiction is confirmed. Changing a library clause never silently edits existing drafts.</Sub>
      {cats.map(cat => (
        <div key={cat} className="mb-6">
          <H2>{cat}</H2>
          <div className="space-y-2">
            {rows.filter(r => r.category === cat).map(c => (
              <Card key={c.id} data-testid={`clause-${c.key}`}>
                <div className="font-medium flex items-center gap-2" style={{ color: 'var(--primary)' }}><BookMarked size={15} /> {c.title}
                  <span className="chip text-xs">{c.jurisdiction}</span>
                  {c.review && <span className="chip text-xs" style={{ background: "#eef", color: "#3a3a7a" }}>{c.review}</span>}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{c.purpose}</div>
                <div className="text-sm mt-2 p-2 rounded" style={{ background: "#faf8f2" }}>{c.text}</div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== BOOKKEEPING & ADMINISTRATION DRAFTING LIBRARY ====================
export function BookkeepingLibraryPage() {
  const [templates, setTemplates] = useState(null);
  const [root, setRoot] = useState([]);
  const [collective, setCollective] = useState([]);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    const [t, r, c] = await Promise.all([A.drafting.bookkeepingTemplates(), A.drafting.root(), A.drafting.collective()]);
    setTemplates(t.data); setRoot(r.data); setCollective(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCollective = (tpl) => setDraft({ Title: tpl.title, template_key: tpl.key, category: "Bookkeeping", Body: "" });
  const saveCollective = async () => {
    try { await A.drafting.createCollective(draft); toast.success("Collective draft created"); setDraft(null); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const promote = async (cid) => { try { await A.drafting.promote(cid); toast.success("Promoted to Root (new version)"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Only admins may promote"); } };

  if (!templates) return <div>Loading…</div>;
  const groups = [...new Set(templates.map(t => t.group))];
  return (
    <div data-testid="bookkeeping-page">
      <H1>Bookkeeping & Administration Drafting</H1>
      <Sub>A whole-system bookkeeping and administration drafting library — separate from the Document Vault. <b>Root Drafting</b> holds authoritative master templates; <b>Collective Drafting</b> holds collaborative work. Collective can never overwrite Root — promotion creates a new, audited Root version. AI never posts journal entries automatically.</Sub>

      <H2>Templates</H2>
      {groups.map(g => (
        <div key={g} className="mb-4">
          <div className="label-caps mb-2">{g}</div>
          <div className="grid md:grid-cols-3 gap-2">
            {templates.filter(t => t.group === g).map(t => (
              <Card key={t.key} data-testid={`bk-tpl-${t.key}`}>
                <div className="font-medium text-sm flex items-center gap-2" style={{ color: 'var(--primary)' }}><Calculator size={14} /> {t.title}</div>
                <button className="btn-outline text-xs mt-2" onClick={() => startCollective(t)} data-testid={`bk-start-${t.key}`}>New collective draft</button>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {draft && (
        <Card className="mb-4" data-testid="bk-draft-form">
          <div className="font-medium mb-2" style={{ color: 'var(--primary)' }}>New Collective Draft — {draft.Title}</div>
          <label className="label-caps">Title</label>
          <input value={draft.Title} onChange={e => setDraft({ ...draft, Title: e.target.value })} data-testid="bk-title" />
          <label className="label-caps mt-2">Body</label>
          <textarea rows={4} value={draft.Body} onChange={e => setDraft({ ...draft, Body: e.target.value })} data-testid="bk-body" />
          <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={saveCollective} data-testid="bk-save">Save to Collective</button><button className="btn-outline" onClick={() => setDraft(null)}>Cancel</button></div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div>
          <H2>Collective Drafting</H2>
          <div className="space-y-2">
            {collective.map(c => (
              <Card key={c.id} data-testid={`collective-${c.id}`}>
                <div className="flex justify-between items-center gap-2">
                  <div><div className="font-medium text-sm" style={{ color: 'var(--primary)' }}>{c.Title}</div>
                    <div className="text-xs" style={{ color: 'var(--muted)' }}>{c.Status}{c.promoted ? " · promoted" : ""}</div></div>
                  {!c.promoted && <button className="btn-gold text-xs flex items-center gap-1" onClick={() => promote(c.id)} data-testid={`promote-${c.id}`}><GitBranch size={12} /> Promote to Root</button>}
                </div>
              </Card>
            ))}
            {collective.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No collective drafts yet.</div></Card>}
          </div>
        </div>
        <div>
          <H2>Root Drafting (authoritative)</H2>
          <div className="space-y-2">
            {root.map(r => (
              <Card key={r.id} data-testid={`root-${r.id}`}>
                <div className="font-medium text-sm" style={{ color: 'var(--primary)' }}>{r.Title} <span className="chip text-xs">v{r.version}</span></div>
                <div className="text-xs" style={{ color: 'var(--muted)' }}>{r.Status} · {r.source_note || "authored"}</div>
              </Card>
            ))}
            {root.length === 0 && <Card><div className="text-sm" style={{ color: 'var(--muted)' }}>No Root drafts yet — promote a collective draft (admin).</div></Card>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== CREDITOR-REMEDY / CHARGING ORDER EDUCATION ====================
export function CreditorRemedyPage() {
  const nav = useNavigate();
  return (
    <div data-testid="creditor-remedy-page">
      <H1>Charging Orders & Creditor-Remedy Education</H1>
      <Sub>Educational overview only. This does <b>not</b> promise creditor-proofing, asset protection, charging-order protection, immunity from judgments, or any guaranteed outcome. No jurisdiction is called "strong" without current, verified sources and stated limitations. Always obtain professional review.</Sub>

      <Card className="mb-3">
        <div className="font-medium flex items-center gap-2 mb-2" style={{ color: 'var(--primary)' }}><ShieldQuestion size={16} /> Core concepts</div>
        <ul className="text-sm space-y-1" style={{ color: 'var(--muted)' }}>
          <li>• <b>Economic interest vs management rights.</b> A charging order typically reaches a debtor-owner's distributions, not management or the entity's assets — but this varies by state and structure.</li>
          <li>• <b>Single- vs multi-member.</b> Some states treat single-member LLCs less protectively than multi-member entities.</li>
          <li>• <b>Foreclosure & bankruptcy.</b> In some jurisdictions a creditor may foreclose on the charged interest or reach it in bankruptcy.</li>
          <li>• <b>Separateness matters.</b> Commingling, undercapitalization, and alter-ego conduct can undermine any structure (veil piercing).</li>
          <li>• <b>Timing is critical.</b> Transfers made after a claim arises can be voidable/fraudulent transfers.</li>
        </ul>
      </Card>

      <Card className="mb-3" style={{ background: "#fdecea" }}>
        <div className="font-medium flex items-center gap-2 mb-1" style={{ color: '#a3271d' }}><AlertTriangle size={16} /> What this portal will never do</div>
        <div className="text-sm" style={{ color: '#a3271d' }}>Guarantee protection, rank jurisdictions without verified sources, or provide legal conclusions. Treat any such claim from any source with skepticism.</div>
      </Card>

      <Card>
        <div className="font-medium mb-2" style={{ color: 'var(--primary)' }}>Run a structured, jurisdiction-aware worksheet</div>
        <p className="text-sm mb-3" style={{ color: 'var(--muted)' }}>Use the Guided Drafting engine's <b>Charging Order Education Worksheet</b> for issue-spotting with source-date and verification fields, then route it for attorney review.</p>
        <button className="btn-primary flex items-center gap-1" onClick={() => nav('/drafting')} data-testid="open-charging-worksheet"><FileSignature size={14} /> Open Guided Drafting</button>
      </Card>
    </div>
  );
}
